import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Download, Trash2, ChevronLeft, ChevronRight, Film, Calendar, Copy, ExternalLink } from 'lucide-react';
import { API_URL } from './App';

export default function GeneratedVideosPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchVideos(1);
  }, []);

  const fetchVideos = async (page) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/user/videos?page=${page}&limit=20`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setVideos(data.videos);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this video?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/user/videos/${videoId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.success) {
        setVideos(videos.filter(vid => vid.id !== videoId));
        if (selectedVideo?.id === videoId) {
          setSelectedVideo(null);
        }
      }
    } catch (error) {
      console.error('Failed to delete video:', error);
    }
  };

  const downloadVideo = async (videoUrl, prompt) => {
    try {
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-genesis-video-${prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}-${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (error) {
      console.error('Failed to download video:', error);
    }
  };

  if (loading && videos.length === 0) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] p-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2 flex items-center">
            <Film className="w-6 h-6 mr-2 text-purple-400" />
            Generated Videos
          </h1>
          <p className="text-gray-400">
            {pagination.total} {pagination.total === 1 ? 'video' : 'videos'} generated
          </p>
        </div>

        {videos.length === 0 ? (
          <div className="text-center py-12 bg-gray-800/30 rounded-lg border border-gray-700">
            <Film className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No videos yet</h3>
            <p className="text-gray-400 mb-6">Start generating videos in the Studio</p>
            <a
              href="/generate"
              className="inline-flex items-center px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
            >
              Go to Studio
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Video List */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-4">
                <h3 className="text-sm font-medium mb-3">All Videos</h3>
                <div className="space-y-2 max-h-[calc(100vh-12rem)] overflow-y-auto pr-2">
                  {videos.map((video) => (
                    <motion.div
                      key={video.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedVideo?.id === video.id
                          ? 'bg-purple-600/20 border border-purple-500/50'
                          : 'bg-gray-800 hover:bg-gray-700'
                      }`}
                      onClick={() => setSelectedVideo(video)}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gray-700 rounded overflow-hidden flex-shrink-0">
                          <video
                            src={video.video_url}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs truncate">{video.prompt}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(video.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                    <button
                      onClick={() => fetchVideos(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-gray-400">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    <button
                      onClick={() => fetchVideos(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Selected Video Player */}
            <div className="lg:col-span-3">
              {selectedVideo ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-gray-800/30 rounded-lg border border-gray-700 p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold mb-1 line-clamp-2">
                        {selectedVideo.prompt}
                      </h2>
                      <div className="flex items-center text-sm text-gray-400">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(selectedVideo.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => downloadVideo(selectedVideo.video_url, selectedVideo.prompt)}
                        className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(selectedVideo.video_url);
                          alert('Video URL copied to clipboard!');
                        }}
                        className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                        title="Copy URL"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => window.open(selectedVideo.video_url, '_blank')}
                        className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                        title="Open in new tab"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteVideo(selectedVideo.id)}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="relative bg-gray-900 rounded-lg overflow-hidden border border-gray-700">
                    <video
                      src={selectedVideo.video_url}
                      controls
                      className="w-full h-auto max-h-[60vh]"
                    />
                  </div>

                  <div className="mt-4 text-sm text-gray-400">
                    <span className="font-medium text-white">Model:</span> {selectedVideo.model || 'Ltxv_13B_0_9_8_Distilled_FP8'}
                  </div>
                </motion.div>
              ) : (
                <div className="bg-gray-800/30 rounded-lg border border-gray-700 p-12 text-center">
                  <Film className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">Select a video</h3>
                  <p className="text-gray-400">Choose a video from the list to view</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}